import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Lightbulb, 
  Brain, 
  RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';

interface DashboardStats {
  totalUsers: number;
  totalCourses: number;
  totalEnrollments: number;
  completionRate: number;
  pendingProposals: number;
  activeInstructors: number;
}

interface ReportData {
  usersByRole: Array<{ name: string; value: number; color: string }>;
  usersByDepartment: Array<{ name: string; count: number }>;
  coursesByCategory: Array<{ name: string; count: number }>;
  enrollmentTrends: Array<{ date: string; enrollments: number; completions: number }>;
  topCourses: Array<{ name: string; enrollments: number }>;
}

interface AIInsight {
  id: string;
  type: 'positive' | 'negative' | 'neutral';
  category: 'courses' | 'users' | 'enrollment' | 'system';
  title: string;
  description: string;
  solution?: string;
  metric?: string;
  impact: 'high' | 'medium' | 'low';
}

interface AIInsightsProps {
  stats: DashboardStats;
  reportData: ReportData;
}

export const AIInsights: React.FC<AIInsightsProps> = ({ stats, reportData }) => {
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    generateInsights();
  }, [stats, reportData]);

  const generateInsights = async () => {
    setLoading(true);
    try {
      const generatedInsights = analyzeData(stats, reportData);
      
      // Enhance with AI-generated insights
      const aiEnhancedInsights = await callAIForInsights(generatedInsights, stats, reportData);
      
      setInsights(aiEnhancedInsights);
    } catch (error) {
      console.error('Error generating insights:', error);
      // Fallback to rule-based insights
      setInsights(analyzeData(stats, reportData));
    } finally {
      setLoading(false);
    }
  };

  const analyzeData = (stats: DashboardStats, reportData: ReportData): AIInsight[] => {
    const insights: AIInsight[] = [];

    // Completion Rate Analysis
    if (stats.completionRate < 50) {
      insights.push({
        id: 'low-completion',
        type: 'negative',
        category: 'courses',
        title: 'Low Course Completion Rate',
        description: `Only ${stats.completionRate.toFixed(1)}% of enrolled users complete their courses.`,
        solution: 'Consider breaking courses into smaller modules, adding more interactive content, or implementing progress tracking notifications.',
        metric: `${stats.completionRate.toFixed(1)}%`,
        impact: 'high'
      });
    } else if (stats.completionRate > 75) {
      insights.push({
        id: 'high-completion',
        type: 'positive',
        category: 'courses',
        title: 'Excellent Course Completion Rate',
        description: `${stats.completionRate.toFixed(1)}% completion rate indicates high-quality content and user engagement.`,
        metric: `${stats.completionRate.toFixed(1)}%`,
        impact: 'high'
      });
    }

    // Instructor to Course Ratio
    const courseToInstructorRatio = stats.totalCourses / Math.max(stats.activeInstructors, 1);
    if (courseToInstructorRatio > 5) {
      insights.push({
        id: 'instructor-overload',
        type: 'negative',
        category: 'system',
        title: 'Instructor Overload Risk',
        description: `${courseToInstructorRatio.toFixed(1)} courses per instructor may impact quality.`,
        solution: 'Recruit more instructors or implement peer-learning programs to distribute teaching load.',
        metric: `${courseToInstructorRatio.toFixed(1)}:1`,
        impact: 'medium'
      });
    }

    // Enrollment Trends
    const recentTrends = reportData.enrollmentTrends.slice(-3);
    if (recentTrends.length >= 2) {
      const trendDirection = recentTrends[recentTrends.length - 1].enrollments - recentTrends[0].enrollments;
      if (trendDirection > 0) {
        insights.push({
          id: 'enrollment-growth',
          type: 'positive',
          category: 'enrollment',
          title: 'Growing Enrollment Trend',
          description: `Enrollments increased by ${trendDirection} in recent months.`,
          metric: `+${trendDirection}`,
          impact: 'medium'
        });
      } else if (trendDirection < -10) {
        insights.push({
          id: 'enrollment-decline',
          type: 'negative',
          category: 'enrollment',
          title: 'Declining Enrollment Trend',
          description: `Enrollments decreased by ${Math.abs(trendDirection)} in recent months.`,
          solution: 'Review course offerings, pricing, or marketing strategies. Consider user feedback surveys.',
          metric: `${trendDirection}`,
          impact: 'high'
        });
      }
    }

    // Course Diversity
    if (reportData.coursesByCategory.length < 3) {
      insights.push({
        id: 'low-diversity',
        type: 'negative',
        category: 'courses',
        title: 'Limited Course Diversity',
        description: 'Few course categories may limit appeal to different user segments.',
        solution: 'Expand into new subject areas based on user demand and industry trends.',
        metric: `${reportData.coursesByCategory.length} categories`,
        impact: 'medium'
      });
    }

    // Top Courses Analysis
    if (reportData.topCourses.length > 0) {
      const topCourse = reportData.topCourses[0];
      const lowPerformingCourses = reportData.topCourses.filter(course => course.enrollments < 5);
      
      if (lowPerformingCourses.length > 0) {
        insights.push({
          id: 'underperforming-courses',
          type: 'negative',
          category: 'courses',
          title: 'Underperforming Courses Identified',
          description: `${lowPerformingCourses.length} courses have very low enrollment.`,
          solution: 'Consider updating content, improving marketing, or retiring low-demand courses.',
          metric: `${lowPerformingCourses.length} courses`,
          impact: 'medium'
        });
      }

      if (topCourse.enrollments > 20) {
        insights.push({
          id: 'popular-course',
          type: 'positive',
          category: 'courses',
          title: 'High-Demand Course Success',
          description: `"${topCourse.name}" shows exceptional popularity with ${topCourse.enrollments} enrollments.`,
          solution: 'Consider creating similar courses or expanding this topic into a series.',
          metric: `${topCourse.enrollments} enrollments`,
          impact: 'medium'
        });
      }
    }

    // Pending Proposals
    if (stats.pendingProposals > 10) {
      insights.push({
        id: 'proposal-backlog',
        type: 'negative',
        category: 'system',
        title: 'High Proposal Backlog',
        description: `${stats.pendingProposals} pending proposals may indicate approval bottleneck.`,
        solution: 'Streamline approval process or delegate review responsibilities.',
        metric: `${stats.pendingProposals} pending`,
        impact: 'medium'
      });
    }

    // User Distribution
    const adminUsers = reportData.usersByRole.find(role => role.name === 'Admin');
    const regularUsers = reportData.usersByRole.find(role => role.name === 'User');
    
    if (adminUsers && regularUsers && adminUsers.value / regularUsers.value > 0.2) {
      insights.push({
        id: 'admin-ratio',
        type: 'negative',
        category: 'users',
        title: 'High Admin to User Ratio',
        description: 'Large number of admin users may indicate permission management issues.',
        solution: 'Review admin permissions and consider role-based access control.',
        impact: 'low'
      });
    }

    return insights;
  };

  const callAIForInsights = async (baseInsights: AIInsight[], stats: DashboardStats, reportData: ReportData): Promise<AIInsight[]> => {
    try {
      const response = await fetch('https://wowscthyngmsphkfzmob.supabase.co/functions/v1/generate-insights', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stats,
          reportData,
          baseInsights
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.insights || baseInsights;
      }
    } catch (error) {
      console.error('AI insights failed, using rule-based insights:', error);
    }
    
    return baseInsights;
  };

  const getInsightIcon = (type: string, category: string) => {
    if (type === 'positive') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (type === 'negative') return <AlertTriangle className="h-5 w-5 text-red-600" />;
    return <Lightbulb className="h-5 w-5 text-blue-600" />;
  };

  const getInsightColor = (type: string) => {
    if (type === 'positive') return 'bg-green-50 border-green-200';
    if (type === 'negative') return 'bg-red-50 border-red-200';
    return 'bg-blue-50 border-blue-200';
  };

  const getBadgeVariant = (impact: string) => {
    if (impact === 'high') return 'destructive';
    if (impact === 'medium') return 'default';
    return 'secondary';
  };

  const positiveInsights = insights.filter(i => i.type === 'positive');
  const negativeInsights = insights.filter(i => i.type === 'negative');
  const neutralInsights = insights.filter(i => i.type === 'neutral');

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-6 w-6 text-primary" />
          <CardTitle>AI Insights & Recommendations</CardTitle>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={generateInsights}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Insights
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all">All Insights</TabsTrigger>
            <TabsTrigger value="positive" className="text-green-700">
              Positive ({positiveInsights.length})
            </TabsTrigger>
            <TabsTrigger value="negative" className="text-red-700">
              Needs Attention ({negativeInsights.length})
            </TabsTrigger>
            <TabsTrigger value="neutral">
              Suggestions ({neutralInsights.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4 mt-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-24 bg-muted rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {insights.map((insight) => (
                  <div
                    key={insight.id}
                    className={`p-4 rounded-lg border ${getInsightColor(insight.type)}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {getInsightIcon(insight.type, insight.category)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-sm">{insight.title}</h4>
                            <Badge variant={getBadgeVariant(insight.impact)}>
                              {insight.impact} impact
                            </Badge>
                            {insight.metric && (
                              <Badge variant="outline">{insight.metric}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {insight.description}
                          </p>
                          {insight.solution && (
                            <div className="text-sm bg-background/50 p-2 rounded border-l-2 border-primary/20">
                              <strong>Recommended Action:</strong> {insight.solution}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="positive" className="space-y-4 mt-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {positiveInsights.map((insight) => (
                <div
                  key={insight.id}
                  className="p-4 rounded-lg border bg-green-50 border-green-200"
                >
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-sm">{insight.title}</h4>
                        {insight.metric && (
                          <Badge variant="outline">{insight.metric}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {insight.description}
                      </p>
                      {insight.solution && (
                        <div className="text-sm mt-2 bg-background/50 p-2 rounded">
                          <strong>Opportunity:</strong> {insight.solution}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="negative" className="space-y-4 mt-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {negativeInsights.map((insight) => (
                <div
                  key={insight.id}
                  className="p-4 rounded-lg border bg-red-50 border-red-200"
                >
                  <div className="flex items-start gap-3">
                    <TrendingDown className="h-5 w-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-sm">{insight.title}</h4>
                        <Badge variant={getBadgeVariant(insight.impact)}>
                          {insight.impact} priority
                        </Badge>
                        {insight.metric && (
                          <Badge variant="outline">{insight.metric}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {insight.description}
                      </p>
                      {insight.solution && (
                        <div className="text-sm bg-background/50 p-2 rounded border-l-2 border-red-300">
                          <strong>Recommended Solution:</strong> {insight.solution}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="neutral" className="space-y-4 mt-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {neutralInsights.map((insight) => (
                <div
                  key={insight.id}
                  className="p-4 rounded-lg border bg-blue-50 border-blue-200"
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-sm">{insight.title}</h4>
                        {insight.metric && (
                          <Badge variant="outline">{insight.metric}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {insight.description}
                      </p>
                      {insight.solution && (
                        <div className="text-sm bg-background/50 p-2 rounded">
                          <strong>Suggestion:</strong> {insight.solution}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};